import { useSoftDeleteFlightEvaluation } from '@src/main/modules/reports/flight/application/hook/useDeleteFlightEvaluation';
import { useDownloadFlightEvaluationPdf } from '@src/main/modules/reports/flight/application/hook/useDownloadFlightEvaluation';
import { BriefFlightEvaluation } from '@src/main/modules/reports/shared/domain/evaluation.interface';
import { getTimestamp } from '@src/main/shared/utils/dateUtils';
import { logger } from '@src/main/shared/utils/logger';
import saveAs from 'file-saver';
import { useCallback, useState } from 'react';

export const useFlightEvaluationActions = () => {
  const { mutateAsync: downloadPdfMutation } = useDownloadFlightEvaluationPdf();
  const { mutateAsync: deleteEvaluationMutation } = useSoftDeleteFlightEvaluation();

  const [evaluationToDelete, setEvaluationToDelete] = useState<BriefFlightEvaluation | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const downloadPdf = useCallback(
    async (evaluation: BriefFlightEvaluation) => {
      try {
        const blob = await downloadPdfMutation(evaluation.id);
        // eslint-disable-next-line max-len
        const fileName = `flight_evaluation_${evaluation.carrierCode}${evaluation.flightNumber}_${evaluation.flightDate}_${evaluation.departureAirport}_${evaluation.arrivalAirport}_${getTimestamp()}.pdf`;
        saveAs(blob, fileName);
      } catch (error) {
        logger.error('Error downloading PDF', error);
      }
    },
    [downloadPdfMutation],
  );

  const handleDeleteEvaluation = useCallback((evaluation: BriefFlightEvaluation) => {
    setEvaluationToDelete(evaluation);
    setIsDeleteModalVisible(true);
  }, []);

  const confirmDeleteEvaluation = useCallback(
    async (onAfterDelete?: () => void) => {
      if (!evaluationToDelete) return;
      try {
        await deleteEvaluationMutation(evaluationToDelete.id);
        onAfterDelete?.();
      } catch (error) {
        logger.error('Error deleting evaluation', error);
      } finally {
        setEvaluationToDelete(null);
        setIsDeleteModalVisible(false);
      }
    },
    [evaluationToDelete, deleteEvaluationMutation],
  );

  const rejectDeleteEvaluation = useCallback(() => {
    setEvaluationToDelete(null);
    setIsDeleteModalVisible(false);
  }, []);

  return {
    downloadPdf,
    handleDeleteEvaluation,
    confirmDeleteEvaluation,
    rejectDeleteEvaluation,
    evaluationToDelete,
    isDeleteModalVisible,
    setIsDeleteModalVisible,
  };
};
